import { sketches } from "@/mocks/sketches";

interface SketchbookRevealProps {
  /** 0 → 1 : overall dark→light transition */
  tone: number;
  /** 0 → 1 : sketches drawing themselves in */
  reveal: number;
}

const smoothstep = (a: number, b: number, x: number) => {
  const t = Math.max(0, Math.min(1, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
};

/**
 * Chapter Three — the sketchbook finale. The stage transitions dark → warm
 * paper, an intro headline rises from below, holds centered, then exits
 * upward. Only after it has cleared does the bold bento sketch grid arrive.
 */
export default function SketchbookReveal({
  tone,
  reveal,
}: SketchbookRevealProps) {
  const lerp = (a: number, b: number) => a + (b - a) * tone;

  // Paper end color: warm off-white (#F5EFE4)
  const r = Math.round(lerp(0, 245));
  const g = Math.round(lerp(0, 239));
  const b = Math.round(lerp(0, 228));
  const paperBg = `rgb(${r}, ${g}, ${b})`;

  // Ink end color: near-black graphite (#1B1815)
  const inkR = Math.round(lerp(255, 27));
  const inkG = Math.round(lerp(255, 24));
  const inkB = Math.round(lerp(255, 21));
  const inkColor = `rgb(${inkR}, ${inkG}, ${inkB})`;

  // Muted ink for captions
  const mutedR = Math.round(lerp(255, 90));
  const mutedG = Math.round(lerp(255, 84));
  const mutedB = Math.round(lerp(255, 76));
  const mutedInk = `rgb(${mutedR}, ${mutedG}, ${mutedB})`;

  // ---- Choreography ----
  const introEnter = smoothstep(0, 0.28, reveal);
  const introExit = smoothstep(0.42, 0.62, reveal);
  const gridReveal = smoothstep(0.6, 0.95, reveal);

  const introY = (1 - introEnter) * 55 - introExit * 55;
  const introFadeIn = Math.min(1, reveal * 5);
  const introFadeOut = 1 - smoothstep(0.5, 0.6, reveal);
  const introOpacity = introFadeIn * introFadeOut;

  // Bento layout definitions — a curated 6-tile set, hand-tuned sizes so
  // each drawing gets real breathing room and one clear hierarchy. The
  // spans MUST sum to the total grid cells (12 cols × 3 rows = 36) so that
  // no tile is pushed outside the row track and clipped by overflow-hidden.
  //
  // Current allocation:
  //   Hero  7 × 3 = 21
  //   T2    3 × 1 = 3
  //   T3    2 × 1 = 2
  //   T4    5 × 1 = 5  ← wide middle-right tile
  //   T5    3 × 1 = 3
  //   T6    2 × 1 = 2
  //                ──
  //                36 ✓
  const bentoTiles = [
    { i: 0, className: "col-span-12 md:col-span-7 md:row-span-3", emphasis: true },
    { i: 1, className: "col-span-6 md:col-span-3 md:row-span-1" },
    { i: 2, className: "col-span-6 md:col-span-2 md:row-span-1" },
    { i: 3, className: "col-span-6 md:col-span-5 md:row-span-1" },
    { i: 4, className: "col-span-6 md:col-span-3 md:row-span-1" },
    { i: 5, className: "col-span-6 md:col-span-2 md:row-span-1" },
  ];

  // Warm paper tones for the notebook "cover" faces.
  const coverFront = "#EFE7D6";
  const coverBack = "#E6DCC6";

  return (
    <div
      className="relative w-full h-full overflow-hidden"
      style={{ backgroundColor: paperBg, color: inkColor }}
    >
      {/* Subtle paper grain vignette */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          opacity: tone * 0.5,
          background:
            "radial-gradient(120% 80% at 50% 50%, rgba(0,0,0,0) 40%, rgba(120,90,50,0.08) 100%)",
        }}
      />

      {/* Top chapter label */}
      <div
        className="absolute top-8 md:top-12 left-8 md:left-14 z-20 flex items-center gap-3 pointer-events-none"
        style={{ opacity: tone }}
      >
        <span
          className="w-8 h-px"
          style={{ backgroundColor: inkColor, opacity: 0.6 }}
        />
        <span
          className="text-[10px] uppercase tracking-[0.4em] whitespace-nowrap"
          style={{ color: mutedInk }}
        >
          Chapter Three · From the Notebook
        </span>
      </div>

      {/* Stage */}
      <div className="relative z-10 h-full w-full" style={{ opacity: tone }}>
        {/* Intro headline block */}
        <div
          className="absolute inset-x-0 flex justify-center px-6 md:px-10"
          style={{
            top: "50%",
            transform: `translate(0, calc(-50% + ${introY}vh))`,
            opacity: introOpacity,
            willChange: "transform, opacity",
            pointerEvents: introOpacity > 0.05 ? "auto" : "none",
          }}
        >
          <div className="flex flex-col items-center text-center max-w-3xl">
            <div
              className="text-[10px] uppercase tracking-[0.4em] mb-5"
              style={{ color: mutedInk }}
            >
              Studio drawings · 2024 — 2025
            </div>

            <h2
              className="font-black uppercase leading-[0.9] tracking-[-0.03em]"
              style={{
                fontSize: "clamp(40px, 5vw, 84px)",
                color: inkColor,
              }}
            >
              BEFORE{" "}
              <span
                className="italic font-light lowercase tracking-tight"
                style={{ color: mutedInk }}
              >
                it glows,
              </span>{" "}
              IT&apos;S A LINE.
            </h2>

            <p
              className="mt-6 max-w-xl text-sm md:text-base leading-relaxed"
              style={{ color: mutedInk }}
            >
              Every fixture in this portfolio begins on paper. Pencil, ruler,
              eraser, coffee ring. A few of the drawings that started the
              rooms you just walked through — still in graphite, not yet warm.
            </p>

            <div
              className="mt-5 text-[10px] uppercase tracking-[0.35em]"
              style={{ color: mutedInk }}
            >
              — Elin V.
            </div>
          </div>
        </div>

        {/* Bento sketch grid */}
        <div
          className="absolute inset-0 flex items-center justify-center px-6 md:px-10 lg:px-14 pt-20 pb-10 overflow-hidden"
          style={{
            opacity: gridReveal,
            willChange: "opacity",
            pointerEvents: gridReveal > 0.1 ? "auto" : "none",
          }}
        >
          <div
            className="w-full max-w-[1280px] mx-auto flex items-center justify-center"
            style={{
              transform: `translateY(${(1 - gridReveal) * 40}px)`,
              willChange: "transform",
            }}
          >
            <div
              className="grid grid-cols-12 gap-3 md:gap-4 w-full"
              style={{
                gridTemplateRows: "repeat(3, minmax(0, 1fr))",
                height: "min(78vh, 780px)",
              }}
            >
              {bentoTiles.map((tile, idx) => {
                const s = sketches[tile.i];
                if (!s) return null;

                const stagger = idx * 0.11;
                const localT = Math.max(
                  0,
                  Math.min(1, (gridReveal - stagger) / 0.5)
                );
                const eased = 1 - Math.pow(1 - localT, 3);

                return (
                  <figure
                    key={s.id}
                    className={`relative group ${tile.className} m-0 min-h-0 min-w-0 cursor-pointer`}
                    style={{
                      opacity: eased,
                      transform: `translateY(${(1 - eased) * 22}px)`,
                      transition:
                        "opacity 140ms linear, transform 140ms linear",
                      perspective: "1600px",
                    }}
                  >
                    <div
                      className="relative w-full h-full rounded-sm"
                      style={{
                        backgroundColor: "#EFE7D6",
                      }}
                    >
                      {/* Layer 1 — the sketch page (sits underneath the cover) */}
                      <div className="absolute inset-0 overflow-hidden rounded-sm">
                        <img
                          src={s.image}
                          alt={`${s.title} — ${s.caption}`}
                          className="absolute inset-0 w-full h-full object-cover object-center mix-blend-multiply"
                          draggable={false}
                        />

                        {/* Tiny caption revealed when the cover is flipped open */}
                        <div
                          className="absolute left-4 bottom-4 md:left-5 md:bottom-5 max-w-[80%] pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-500"
                        >
                          <div
                            className="text-[10px] uppercase tracking-[0.35em] mb-1"
                            style={{ color: mutedInk, opacity: 0.9 }}
                          >
                            {s.index}
                          </div>
                          <div
                            className={`font-semibold tracking-tight leading-tight ${
                              tile.emphasis
                                ? "text-[18px] md:text-[22px]"
                                : "text-[13px] md:text-[14px]"
                            }`}
                            style={{ color: inkColor }}
                          >
                            {s.title}
                          </div>
                          {tile.emphasis && (
                            <div
                              className="text-[12px] md:text-[13px] mt-1 leading-snug"
                              style={{ color: mutedInk }}
                            >
                              {s.caption}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Layer 2 — the notebook page "cover", hinged on the LEFT
                          edge. Flips open on hover like turning a page. */}
                      <div
                        className="absolute inset-0 rounded-sm transition-transform duration-[1100ms] ease-[cubic-bezier(0.65,0.05,0.36,1)] group-hover:[transform:rotateY(-168deg)]"
                        style={{
                          transformOrigin: "left center",
                          transformStyle: "preserve-3d",
                          willChange: "transform",
                        }}
                      >
                        {/* Front of the page (label side) */}
                        <div
                          className="absolute inset-0 rounded-sm overflow-hidden"
                          style={{
                            backgroundColor: coverFront,
                            backfaceVisibility: "hidden",
                            WebkitBackfaceVisibility: "hidden",
                            backgroundImage:
                              "radial-gradient(120% 90% at 30% 20%, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0) 55%), radial-gradient(140% 100% at 80% 100%, rgba(120,90,50,0.10) 0%, rgba(120,90,50,0) 60%)",
                          }}
                        >
                          {/* Binding hairline on the left edge (the "spine") */}
                          <span
                            aria-hidden
                            className="absolute left-0 top-0 bottom-0 w-px"
                            style={{
                              background:
                                "linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.18) 20%, rgba(0,0,0,0.18) 80%, rgba(0,0,0,0) 100%)",
                            }}
                          />
                          {/* Faint horizontal ruled line, sketchbook feel */}
                          <span
                            aria-hidden
                            className="absolute left-6 right-6 top-10 h-px"
                            style={{
                              background:
                                "linear-gradient(to right, rgba(0,0,0,0) 0%, rgba(0,0,0,0.12) 20%, rgba(0,0,0,0.12) 80%, rgba(0,0,0,0) 100%)",
                            }}
                          />
                          {/* Subtle corner "curl" shadow on the right, hints at
                              a page you can lift */}
                          <span
                            aria-hidden
                            className="absolute top-0 right-0 w-10 h-10"
                            style={{
                              background:
                                "linear-gradient(225deg, rgba(0,0,0,0.10) 0%, rgba(0,0,0,0) 55%)",
                              opacity: 0.9,
                            }}
                          />

                          {/* Label */}
                          <div
                            className={`absolute inset-0 flex flex-col justify-between ${
                              tile.emphasis ? "p-6 md:p-8" : "p-4 md:p-5"
                            }`}
                          >
                            <div
                              className="text-[10px] uppercase tracking-[0.4em]"
                              style={{ color: mutedInk }}
                            >
                              {s.index}
                            </div>

                            <div>
                              <div
                                className={`font-semibold tracking-tight leading-tight ${
                                  tile.emphasis
                                    ? "text-[20px] md:text-[26px]"
                                    : "text-[14px] md:text-[16px]"
                                }`}
                                style={{ color: inkColor }}
                              >
                                {s.title}
                              </div>
                              {tile.emphasis && (
                                <div
                                  className="text-[12px] md:text-[13px] mt-1 leading-snug"
                                  style={{ color: mutedInk }}
                                >
                                  {s.caption}
                                </div>
                              )}
                              <div
                                className="mt-3 flex items-center gap-2 text-[9px] uppercase tracking-[0.35em]"
                                style={{ color: mutedInk, opacity: 0.75 }}
                              >
                                <span
                                  className="w-4 h-px"
                                  style={{ backgroundColor: mutedInk, opacity: 0.6 }}
                                />
                                <span className="whitespace-nowrap">Turn to view</span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Back of the page (visible mid-flip) — plain warm
                            paper, slightly darker, no label. */}
                        <div
                          className="absolute inset-0 rounded-sm overflow-hidden"
                          style={{
                            backgroundColor: coverBack,
                            transform: "rotateY(180deg)",
                            backfaceVisibility: "hidden",
                            WebkitBackfaceVisibility: "hidden",
                            backgroundImage:
                              "radial-gradient(140% 100% at 20% 30%, rgba(255,255,255,0.35) 0%, rgba(255,255,255,0) 55%), radial-gradient(140% 100% at 80% 100%, rgba(60,40,20,0.10) 0%, rgba(60,40,20,0) 60%)",
                          }}
                        >
                          {/* Binding hairline on the right edge now (mirrored) */}
                          <span
                            aria-hidden
                            className="absolute right-0 top-0 bottom-0 w-px"
                            style={{
                              background:
                                "linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.18) 20%, rgba(0,0,0,0.18) 80%, rgba(0,0,0,0) 100%)",
                            }}
                          />
                        </div>
                      </div>

                      {/* Soft shadow cast onto the sketch as the page lifts,
                          adds depth to the flip. */}
                      <div
                        aria-hidden
                        className="absolute inset-y-0 left-0 w-1/3 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-[900ms] rounded-l-sm"
                        style={{
                          background:
                            "linear-gradient(to right, rgba(20,18,15,0.28) 0%, rgba(20,18,15,0) 100%)",
                        }}
                      />
                    </div>
                  </figure>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}